// Formatting helpers for the jobs page, ported 1:1 from ionq-cloud-ui
// (src/util/format.tsx + src/components/shared/utils/utils.ts) so the ported
// page renders byte-identical cell text.

export interface CurrencyFormatter {
    format: (value: number) => string;
}

export const USD: Intl.NumberFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const DECIMAL: Intl.NumberFormat = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
});

export const ACU: CurrencyFormatter = {
    format: (value: number) => `❖${DECIMAL.format(value)}`,
};

// Accepts both the legacy 'acu' unit value and its customer-facing 'credits'
// relabeling, matching the remote's isCreditsUnit.
export const isCreditsUnit = (unit: string | undefined): boolean => {
    const normalized = unit?.toLowerCase();
    return normalized === 'acu' || normalized === 'credits';
};

export const getFormatterFromUnit = (
    unit: string | undefined,
): CurrencyFormatter => {
    if (isCreditsUnit(unit)) return ACU;
    return USD;
};

export function formatCurrency(value: number, unit?: string): string {
    return getFormatterFromUnit(unit).format(value);
}

// yyyy-mm-dd — the 'sv' locale renders exactly this shape.
const YMD = new Intl.DateTimeFormat('sv', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

// hh:mm:ss TZ — 2-digit fields, 24h clock, short timezone name.
const YMDHMS = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
    hourCycle: 'h23',
});

const isRenderableUnix = (value: unknown): value is number =>
    typeof value === 'number' && value >= 0 && value <= Number.MAX_SAFE_INTEGER;

/** `2026-08-24 13:37:05 EDT` for a unix-seconds timestamp ('' when absent). */
export const formatJobDate = (unixSeconds: number | undefined): string => {
    if (!isRenderableUnix(unixSeconds)) return '';
    const date = new Date(unixSeconds * 1000);
    return `${YMD.format(date)} ${YMDHMS.format(date)}`;
};

// humanize-duration's default unit lengths (ms): the remote formats the full
// duration with that library and keeps only the first comma-segment — i.e.
// the largest unit, floored. Reproducing that segment locally avoids the
// dependency while rendering the same text.
const HUMANIZE_UNITS: Array<[label: string, ms: number]> = [
    ['year', 31557600000],
    ['month', 2629800000],
    ['week', 604800000],
    ['day', 86400000],
    ['hour', 3600000],
    ['minute', 60000],
    ['second', 1000],
];

/** Largest-unit duration ("3 days"), matching humanize-duration's leading
 * segment for the remote's `${...} ago` tooltip. */
export const humanizeLargestUnit = (ms: number): string => {
    for (const [label, unitMs] of HUMANIZE_UNITS) {
        const count = Math.floor(ms / unitMs);
        if (count >= 1) return `${count} ${label}${count === 1 ? '' : 's'}`;
    }
    // Sub-second durations: humanize-duration says "N milliseconds".
    const msCount = Math.floor(ms);
    return `${msCount} millisecond${msCount === 1 ? '' : 's'}`;
};

/** Elapsed-time label for the date cell tooltip; caller appends " ago". */
export const getTooltipFormattedDate = (
    unixSeconds: number | undefined,
    now: number = Date.now(),
): string => {
    if (!isRenderableUnix(unixSeconds)) return '';
    return humanizeLargestUnit(now - unixSeconds * 1000);
};

/** Truncated id shown in the ID column (copy uses the full value). */
export const getIdDisplay = (value: string) => value.slice(-8);
